import React, { Component, Fragment } from 'react';
import { Routes, Route, Link, useNavigate, useNavigationType } from 'react-router-dom';
import { Container, Divider, Grid, Header, Image, List, Segment, Modal, Button, Icon, Form } from 'semantic-ui-react';
import IdleTimer from 'react-idle-timer';
import Configuration from './config/Configuration.js';
import HealthMatchBody from './components/HealthMatchBody';
import ZipsterMenu from './components/ZipsterMenu';
import RegistrationForm from './components/RegistrationForm';
import WeAreLinkModal from './weare/WeAreLinkModal.jsx';
import { withTranslation } from 'react-i18next';
import detectBrowserLanguage from 'detect-browser-language';
import { createMedia } from "@artsy/fresnel";
import { useKeycloak } from '@react-keycloak/web';
import authenticationService from './api/AuthenticationServiceInstance.js';

import './App.css';
import logo from './images/logo-colour-small.png';
import i18n from "./i18n";
import PublicServices from './components/PublicServices.jsx';
import AnonymousChatbotPage from "./components/chatbot/AnonymousChatbotPage.jsx";

const AppMedia = createMedia({
  breakpoints: {
    mobile: 320,
    tablet: 768,
    computer: 992,
    largeScreen: 1200,
    widescreen: 1920
  }
});

const { Media, MediaContextProvider } = AppMedia;

const PUBLIC_PATHS = ["/register", "/publicservices", "/zippy"];

class App extends Component {

  constructor(props) {
    super(props);
    this.idleTimer = null;
    this.config = new Configuration();
    this.state = {
      timeout: 1200000,
      isIdle: false,
      showLogoutModal: false,
      activeUser: null,
      userType: null,
      showFeedbackModal: false,
      feedbackText: '',
      showWeAreModal: false,
      isWeAreLinked: false,
    }
    // Bind event handlers and methods
    this.onIdle = this.onIdle.bind(this)
  }

  createFeedback(notes) {
    const currentUrl = window.location.href;
    return fetch(this.config.FEEDBACK_URL, {
      method: 'POST',
      body: JSON.stringify({
        authored: new Date().toISOString(),
        notes: notes,
        practitionerRole: this.props.keycloak?.tokenParsed?.uuid,
        location: currentUrl
      })
    }).then(response => {
      if (!response.ok) {
        let errorResponse = this.handleResponseError(response);
        return errorResponse;
      }
      return response.json();
    }).catch(error => {
      this.handleError(error);
    });
  }

  detectLanguage = () => {
    var languageDetected = false;
    if (!languageDetected) {
      var language = detectBrowserLanguage();
      if (language !== null) {
        if (language.startsWith("nl")) {
          this.setDutch();
        } else if (language.startsWith("fr")) {
          this.setFrench();
        } else {
          this.setDutch();
        }
      }
    }
  }

  getLanguage = () => {
    var language = detectBrowserLanguage();
    if (language !== null) {
      if (i18n.language.startsWith("nl")) {
        return "Français";
      } else if (i18n.language.startsWith("fr")) {
        return "Nederlands";
      } else {
        return "Français";
      }
    }
  }

  changeLanguage = () => {
    var language = detectBrowserLanguage();
    if (language !== null) {
      if (i18n.language.startsWith("nl")) {
        this.setFrench();
      } else if (i18n.language.startsWith("fr")) {
        this.setDutch();
      } else {
        return "Français";
      }
    }
  }

  setFrench = () => {
    i18n.changeLanguage('fr');
  }

  setDutch = () => {
    i18n.changeLanguage('nl');
  }

  async componentDidMount() {
    await authenticationService.init(async (authenticated) => {
      if (authenticated) {
        const userType = authenticationService.getUserType();
        this.setState({ userType: userType });
        if (userType === "practitioner") {
          const user = authenticationService.getActiveUser();
          this.setState({ activeUser: user });
        } else if (userType === "patient") {
          const user = authenticationService.getActiveUser();
          const linked = localStorage.getItem("weare_linked") === "true";
          if (!linked) {
            this.setState({ showWeAreModal: true });
          }
          this.setState({ activeUser: user });
        }
      }
    });
    this.detectLanguage();
  }

  handleWeAreLinked = (data) => {
    // data = { linked: true, webId: "...", ... } uit je back-end (stap 6)
    localStorage.setItem("weare_linked", "true");
    this.setState({ isWeAreLinked: true });
  };

  onIdle() {

  }

  logout = () => {
    this.setState({ showLogoutModal: false });
    this.props.keycloak.logout();
    this.setState({ isIdle: false, activeUser: null, userType: null })
  }

  getRegistrationPage = () => {
    return <RegistrationForm />;
  }

  getPublicServicesPage = () => {
    return <PublicServices />;
  }

  getBody = () => {
    const { userType, activeUser } = this.state;
    if (!userType) return null;
    return <HealthMatchBody logout={this.logout}
      activeUser={activeUser}
      userType={userType} />;
  }

  getTermsOfUse = () => {
    if (i18n.language.startsWith("fr")) {
      return 'https://www.zipster.care/conditions-dutilisation/';
    } else {
      return 'https://www.zipster.care/gebruikersvoorwaarden/';
    }
  }

  getPrivacyPolicy = () => {
    if (i18n.language.startsWith("fr")) {
      return 'https://www.zipster.care/politique-de-confidentialite/';
    } else {
      return 'https://www.zipster.care/privacybeleid/';
    }
  }

  getFooter = () => {
    const { t } = this.props;
    return <MediaContextProvider>
      <Segment color='blue' inverted vertical style={{ margin: '5em 0em 0em', padding: '5em 0em' }}>
        <Container textAlign='center'>
          <Container as={Media} greaterThanOrEqual="computer">
            <Grid divided inverted stackable>
              <Grid.Column width={5}>
                <Header inverted as='h4' content={t("My Profile")} />
                <List link inverted>
                  <List.Item as={Link} to='/profile'>{t("My Profile")}</List.Item>
                  <List.Item as={Link} to='/organisation'>{t("My Organisation")}</List.Item>
                  <List.Item as={Link} onClick={this.changeLanguage}>{this.getLanguage()}</List.Item>
                  <List.Item onClick={this.logout} as='a'>{t("Log Out")}</List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={5}>
                <Header inverted as='h4' content={t("About Us")} />
                <List link inverted>
                  <List.Item as={Link} to='/contact'>{t("Contact Us Main")}</List.Item>
                  <List.Item as={Link} to='/faq'>{t("FAQ")}</List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={6}>
                <Header inverted as='h4' style={{ fontFamily: 'Montserrat' }} content='zipster.care' />
                <p>
                  {t("Tagline")}
                </p>
              </Grid.Column>
            </Grid>
            <Divider inverted section />
          </Container>
          <Image centered size='tiny' src={logo} />
          <Container as={Media} greaterThanOrEqual="computer">
            <List horizontal inverted divided link size='small'>
              <List.Item as={Link} to='/contact'>
                {t("Contact Us Main")}
              </List.Item>
              <List.Item as='a' href={this.getTermsOfUse()} target='_blank'>
                {t("Terms Of Use")}
              </List.Item>
              <List.Item as='a' href={this.getPrivacyPolicy()} target='_blank'>
                {t("Privacy Policy")}
              </List.Item>
            </List>
          </Container>
          <Container as={Media} lessThan="computer">
            <Divider hidden />
            <List inverted link relaxed size='small' >
              <List.Item onClick={this.logout} as='a'>{t("Log Out")}</List.Item>
            </List>
          </Container>
        </Container>
      </Segment>
    </MediaContextProvider>;
  }

  handleOpenFeedbackModal = () => {
    this.setState({ showFeedbackModal: true });
  };

  handleCloseFeedbackModal = () => {
    this.setState({ showFeedbackModal: false });
  };

  handleFeedbackChange = (e, { value }) => {
    this.setState({ feedbackText: value });
  };

  handleSubmitFeedback = () => {
    // Call the createFeedback method with the feedback text
    this.createFeedback(this.state.feedbackText)
      .then(() => {
        // Optionally show a success message or clear the feedback form
        this.setState({ feedbackText: '', showFeedbackModal: false });
      })
      .catch((error) => {
        // Handle any errors that occur during feedback submission
        console.error("Error submitting feedback:", error);
      });
  }


  getFeedbackModal = () => {
    const { t } = this.props;
    if (!this.state.showFeedbackModal) return null;
    return (
      <Modal open={this.state.showFeedbackModal} onClose={this.handleCloseFeedbackModal}>
        <Modal.Header>{t("Your Feedback Matters")}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.TextArea
              label={t("Your Experience")}
              placeholder={t("Your Experience Placeholder")}
              value={this.state.feedbackText}
              onChange={this.handleFeedbackChange}
              rows={10}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color='blue' onClick={this.handleCloseFeedbackModal}>
            {t("Cancel")}
          </Button>
          <Button icon='checkmark' labelPosition='right' content={t("Send")} onClick={this.handleSubmitFeedback} />
        </Modal.Actions>
      </Modal>
    );
  }

  getHomePage = () => {
    const { userType, activeUser } = this.state;

    if (!userType) return null; // Wacht tot userType geladen is
    return <div>
      <ZipsterMenu activeUser={activeUser} userType={userType} />
      {this.getBody()}
      <WeAreLinkModal
        open={this.state.showWeAreModal}
        onClose={() => this.setState({ showWeAreModal: false })}
        onLinked={this.handleWeAreLinked}
      />
      {this.getFooter()}
      <MediaContextProvider>
        <Media at='mobile'>
          {this.getFeedbackModal()}
          <Button
            icon='comment'
            color='blue'
            circular
            size='large'
            style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}
            onClick={this.handleOpenFeedbackModal}
          />
        </Media>
      </MediaContextProvider>

    </div>;
  }

  render() {
    console.log('Rendering app.js');
    const { t, keycloak } = this.props;
    const tokenParsed = keycloak?.tokenParsed;
    console.log('🎬 App render:', {
      authenticated: keycloak?.authenticated,
      activeUser: this.state.activeUser,
      tokenParsed: keycloak?.tokenParsed
    });
    if (this.state.activeUser || tokenParsed) {
      return <Fragment>
        <Modal
          open={this.state.showLogoutModal}
          onClose={this.logout}
          basic
          size='small'
          dimmer='blurring'>
          <Header icon='lock' content={t("Session Expired")} />
          <Modal.Content>
            <h3>{t("Login Again")}</h3>
          </Modal.Content>
          <Modal.Actions>
            <Button color='blue' onClick={this.logout} inverted>
              <Icon name='checkmark' />OK
            </Button>
          </Modal.Actions>
        </Modal>
        <IdleTimer
          ref={ref => { this.idleTimer = ref }}
          onIdle={this.onIdle}
          timeout={this.state.timeout}
          startOnLoad />
        <Routes>
          <Route path="/*" element={this.getHomePage()} />
        </Routes>
      </Fragment>;
    } else {
      return <Routes>
        <Route path="/zippy" element={<AnonymousChatbotPage />} />
        <Route path="/register" element={<RegistrationForm />} />
        <Route path="/publicservices" element={<PublicServices />} />
        <Route path="*" element={<WithNavigateWrapper />} />
      </Routes>;
    }
  }

}

function WithNavigateWrapper(props) {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();

  const pathname = window.location.pathname;
  const isPublicRoute = PUBLIC_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );

  // Public route: GEEN keycloak afdwingen
  if (isPublicRoute) {
    return <App {...props} navigate={navigate} keycloak={keycloak} />;
  }

  // Private routes: keycloak moet klaar zijn
  if (!initialized) {
    return <div>Loading Keycloak...</div>;
  }

  // Private routes: login afdwingen
  if (!keycloak.authenticated) {
    keycloak.login({ redirectUri: window.location.href });
    return <div>Redirecting to login...</div>;
  }

  return <App {...props} navigate={navigate} keycloak={keycloak} />;
}

export default withTranslation()(WithNavigateWrapper);